import React from 'react';
import { getButtonClasses } from '../../styles/buttonStyles';

/**
 * Unified Button Component
 * 
 * @param {Object} props
 * @param {'primary' | 'secondary' | 'outline' | 'ghost' | 'icon' | 'danger' | 'success' | 'warning' | 'info'} props.variant - Button style variant
 * @param {'blue' | 'green' | 'red' | 'indigo' | 'purple' | 'gray' | 'white'} props.color - Button color
 * @param {'xs' | 'sm' | 'md' | 'lg' | 'xl'} props.size - Button size
 * @param {React.ReactNode} props.children - Button content
 * @param {React.ReactNode} props.icon - Icon component (from lucide-react)
 * @param {'left' | 'right'} props.iconPosition - Position of the icon
 * @param {boolean} props.loading - Show loading state
 * @param {boolean} props.disabled - Disable button
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onClick - Click handler
 * @param {string} props.type - Button type (button, submit, reset)
 * @param {string} props.title - Tooltip text
 */
const Button = ({
    variant = 'primary',
    color = 'blue',
    size = 'md',
    children,
    icon,
    iconPosition = 'left',
    loading = false,
    disabled = false,
    className = '',
    onClick,
    type = 'button',
    title,
    ...rest
}) => {
    const buttonClasses = getButtonClasses(variant, color, size, className);

    const iconSize = size === 'xs' ? 14 : size === 'sm' ? 16 : size === 'lg' ? 20 : size === 'xl' ? 22 : 16;

    const LoadingSpinner = () => (
        <svg
            className="animate-spin"
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
        >
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
        </svg>
    );

    const renderIcon = () => {
        if (loading) {
            return <LoadingSpinner />;
        }
        if (icon) {
            return React.cloneElement(icon, { size: iconSize });
        }
        return null;
    };

    return (
        <button
            type={type}
            className={buttonClasses}
            onClick={onClick}
            disabled={disabled || loading}
            title={title}
            {...rest}
        >
            {iconPosition === 'left' && renderIcon() && (
                <span className={children ? 'mr-2' : ''}>{renderIcon()}</span>
            )}
            {children}
            {iconPosition === 'right' && renderIcon() && (
                <span className={children ? 'ml-2' : ''}>{renderIcon()}</span>
            )}
        </button>
    );
};

export default Button;
